import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Building2,
  CalendarDays,
  DoorOpen,
  GraduationCap,
  Music2,
  Music4,
  Users,
} from 'lucide-react'

import { locationsApi, roomsApi, scheduleApi, studentsApi, teachersApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { isAdmin, isOwner, isTeacher } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Card, CardHeader, StatCard } from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { InstrumentBadges } from '@/components/ui/InstrumentBadge'
import { useModuleEnabled } from '@/hooks/useModules'
import { greeting, plural } from '@/lib/format'
import { toIsoDate } from '@/lib/scheduleTime'

export function DashboardPage() {
  const { user } = useAuth()
  const canSeeAccounts = isAdmin(user)
  const teacherMode = isTeacher(user)

  // Pulpit prowadzi do sekcji, więc skróty do wyłączonych modułów muszą zniknąć
  // razem z nimi — inaczej kierowałyby prosto w komunikat o wyłączeniu.
  const studentsOn = useModuleEnabled('uczniowie')
  const teachersOn = useModuleEnabled('nauczyciele')
  const roomsOn = useModuleEnabled('sale')
  const scheduleOn = useModuleEnabled('grafik')

  const locationsQuery = useQuery({
    queryKey: ['locations', 'summary'],
    queryFn: () => locationsApi.list({ size: 1 }),
  })

  const studentsQuery = useQuery({
    queryKey: ['students', 'summary'],
    queryFn: () => studentsApi.list({ size: 5, status: 'ACTIVE' }),
    enabled: studentsOn,
  })

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'summary'],
    queryFn: () => roomsApi.list({ size: 1 }),
    enabled: roomsOn,
  })

  const teachersQuery = useQuery({
    queryKey: ['teachers', 'summary'],
    queryFn: () => teachersApi.list({ size: 1 }),
    enabled: canSeeAccounts && teachersOn,
  })

  // Nauczyciel dostaje z backendu wyłącznie własne zajęcia, więc to samo
  // zapytanie daje każdemu liczbę, która go dotyczy.
  const dzis = toIsoDate(new Date())
  const lessonsQuery = useQuery({
    queryKey: ['schedule', 'dzisiaj', dzis],
    queryFn: () =>
      scheduleApi.lessons({ date_from: dzis, date_to: dzis, include_cancelled: false }),
    enabled: scheduleOn,
  })

  const locationCount = locationsQuery.data?.total ?? 0
  const scopeLabel = isOwner(user)
    ? 'wszystkich oddziałach'
    : plural(locationCount, 'swoim oddziale', 'swoich oddziałach', 'swoich oddziałach')

  const students = studentsQuery.data?.items ?? []

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user?.first_name ?? ''}`}
        description={
          teacherMode
            ? 'Oto podsumowanie Twojej pracy w szkole.'
            : `Oto podsumowanie sytuacji w ${scopeLabel}.`
        }
      />

      <section
        className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
        aria-label="Podsumowanie"
      >
        {studentsOn && (
          <StatCard
            label={teacherMode ? 'Twoi uczniowie' : 'Aktywni uczniowie'}
            value={studentsQuery.isPending ? '—' : String(studentsQuery.data?.total ?? 0)}
            icon={<Music4 size={22} />}
            tone="primary"
            to="/uczniowie"
          />
        )}

        {canSeeAccounts && teachersOn ? (
          <StatCard
            label="Nauczyciele"
            value={teachersQuery.isPending ? '—' : String(teachersQuery.data?.total ?? 0)}
            icon={<GraduationCap size={22} />}
            to="/nauczyciele"
          />
        ) : (
          <StatCard
            label={isOwner(user) ? 'Oddziały' : 'Twoje oddziały'}
            value={locationsQuery.isPending ? '—' : String(locationCount)}
            icon={<Building2 size={22} />}
            // Listę oddziałów prowadzi wyłącznie właścicielka; pozostali
            // dostaliby tu „brak dostępu”, więc kafelek zostaje bez odnośnika.
            to={isOwner(user) ? '/oddzialy' : undefined}
          />
        )}

        {roomsOn && (
          <StatCard
            label="Sale"
            value={roomsQuery.isPending ? '—' : String(roomsQuery.data?.total ?? 0)}
            icon={<DoorOpen size={22} />}
            to="/sale"
          />
        )}

        {scheduleOn && (
          <StatCard
            label={teacherMode ? 'Twoje lekcje dzisiaj' : 'Lekcje dzisiaj'}
            value={lessonsQuery.isPending ? '—' : String(lessonsQuery.data?.length ?? 0)}
            icon={<CalendarDays size={22} />}
            to="/grafik"
          />
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        {studentsOn && (
          <Card className="lg:col-span-2">
            <CardHeader
              title={teacherMode ? 'Twoi uczniowie' : 'Ostatnio dodani uczniowie'}
              action={
                <Link
                  to="/uczniowie"
                  className="text-xs font-bold text-primary transition-opacity hover:opacity-70"
                >
                  Zobacz wszystkich
                </Link>
              }
            />
            {students.length === 0 ? (
              <p className="p-6 text-sm text-on-surface-variant">
                Nie ma jeszcze żadnych aktywnych uczniów.{' '}
                <Link to="/uczniowie" className="font-bold text-primary underline">
                  Dodaj pierwszego
                </Link>
                .
              </p>
            ) : (
              <ul className="divide-y divide-outline/10">
                {students.map((student) => (
                  <li key={student.id}>
                    <Link
                      to={`/uczniowie/${student.id}`}
                      className="flex items-center gap-3 px-6 py-4 transition-colors hover:bg-surface-container-low"
                    >
                      <Avatar name={student.full_name} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-bold text-on-surface">{student.full_name}</p>
                        <p className="text-xs text-on-surface-variant">
                          {student.teacher?.full_name ?? 'Bez prowadzącego'} ·{' '}
                          {student.location.name}
                        </p>
                      </div>
                      <InstrumentBadges instruments={student.instruments} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        <div className="space-y-6">
          <Card>
            <CardHeader title="Skróty" />
            <div className="space-y-2 p-6">
              {studentsOn && (
                <Shortcut to="/uczniowie" icon={<Music4 size={18} />} label="Baza uczniów" />
              )}
              {teachersOn && canSeeAccounts && (
                <Shortcut
                  to="/nauczyciele"
                  icon={<GraduationCap size={18} />}
                  label="Nauczyciele"
                />
              )}
              {roomsOn && <Shortcut to="/sale" icon={<DoorOpen size={18} />} label="Sale" />}
              {canSeeAccounts && (
                <Shortcut to="/konta" icon={<Users size={18} />} label="Zarządzaj kontami" />
              )}
              {isOwner(user) && (
                <>
                  <Shortcut
                    to="/oddzialy"
                    icon={<Building2 size={18} />}
                    label="Zarządzaj oddziałami"
                  />
                  <Shortcut
                    to="/instrumenty"
                    icon={<Music2 size={18} />}
                    label="Słownik instrumentów"
                  />
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

function Shortcut({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-primary"
    >
      <span aria-hidden="true">{icon}</span>
      {label}
    </Link>
  )
}
